import { BankTxnStatus, type JournalEntry } from "@/apis/gql/types";
import { useTable } from "@/components/shared/data-table/useTable";
import { type RawFilters, useServerSideQuery } from "@/hooks/useQueryFilters";
import { useTablePagination } from "@/hooks/useTablePagination";
import type { ExpandedState, Row } from "@tanstack/react-table";
import { useState } from "react";

import { useGetAllJournalEntries } from "@/queries/ledger/get-all";

import { getExpandedRowModel } from "@/lib/table/row-expanding";

import { columns } from "./columns";

export type FilterFields =
  | "documentType"
  | "documentReference"
  | "bankTransactionStatus"
  | "datevExportId";

export type BankBookingsTableInstance = ReturnType<typeof useBankBookingsTable>;

const defaultFilters: RawFilters<FilterFields> = {
  documentType: "bank-transaction",
  bankTransactionStatus: [
    BankTxnStatus.REVIEWED,
    BankTxnStatus.EXPORT_FAILED,
    BankTxnStatus.EXPORTING,
  ],
};

export function useBankBookingsTable({
  tableName,
  queryKeyPrefix,
  storageKey,
}: {
  tableName: string;
  queryKeyPrefix: string;
  storageKey?: string;
}) {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const { page, setPage, limit, setLimit } = useTablePagination();

  const {
    globalSearch,
    setGlobalSearch,
    serverSideFilters,
    setFilters,
    filters,
    sorting,
    onSortingChange,
    sort,
    order,
    resetFilters,
    initialServerSideFilters,
  } = useServerSideQuery<FilterFields>({
    storageKey,
    defaultFilters,
    queryKeyPrefix,
    defaultSorting: { sort: "createdAt", order: "desc" },
  });

  const { data, loading, error, pagination } = useGetAllJournalEntries({
    query: serverSideFilters,
    global: globalSearch,
    page,
    limit,
    offset: (page - 1) * limit,
    sort,
    order,
    fetchPolicy: "cache-and-network",
  });

  const table = useTable({
    data: data ?? [],
    columns,
    tableName,
    expanded,
    onExpandedChange: setExpanded,
    getRowId: (row) => row.journalEntryId,
    getExpandedRowModel,
    sorting,
    onSortingChange,
    manualFiltering: true,
    manualSorting: true,
    manualPagination: true,
    enableSubRowSelection: false,
    serverSideFilters,
    initialServerSideFilters,
    globalServerSideSearch: globalSearch,
    onServerSideFiltersClear: resetFilters,
  });

  const handleRowClick = (
    event: React.MouseEvent,
    row: Row<JournalEntry>,
    index: number,
  ) => {
    table.setRowSelection((prev: Record<string, boolean>) => {
      const isSelected = !!prev[row.id];
      const selectedCount = Object.values(prev).filter(Boolean).length;

      return { [row.id]: !isSelected || selectedCount !== 1 };
    });
  };

  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const handlePageSizeChange = (pageSize: number) => {
    setLimit(pageSize);
  };

  return {
    ...table,
    loading,
    error,
    pagination,
    handleRowClick,
    handlePageChange,
    handlePageSizeChange,
    expanded,
    setExpanded,
    globalSearch,
    setGlobalSearch,
    filters,
    setFilters,
    limit,
  };
}
